import { FC, useEffect, useRef, useState } from 'react';
import { Document, Page } from 'react-pdf';

import Loader from '../../../../components/loader/index';
import { configurePdfWorker } from '../../../../utils/pdf-worker.util';

configurePdfWorker();

// The design draws the page at A4, which is what a PDF point-for-point is at 100%.
export const PAGE_WIDTH_PX = 595;

/**
 * The document itself, every page of it, at the zoom the header is showing.
 *
 * All the pages are rendered rather than one at a time: a reader scrolls a document, and the page
 * number in the header is a readout of where they are, not a control they drive. Which page that
 * is comes from an observer on the pages themselves rather than from arithmetic on the scroll
 * offset, because pages differ in height — a deck of landscape slides with one portrait appendix
 * would make any uniform-height guess drift.
 */
export const FilePreviewDocument: FC<{
  onPageChange: (page: { current: number; total: number } | null) => void;
  url: string;
  zoom: number;
}> = ({ onPageChange, url, zoom }) => {
  const [pageCount, setPageCount] = useState(0);
  const pageRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    if (!pageCount) return;

    // The topmost page still in view is the one being read; a page leaving the top hands over to
    // the one below it.
    const visible = new Set<number>();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const page = Number((entry.target as HTMLElement).dataset.page);

          if (entry.isIntersecting) visible.add(page);
          else visible.delete(page);
        }

        const current = visible.size ? Math.min(...visible) : 1;

        onPageChange({ current, total: pageCount });
      },
      { threshold: 0.1 },
    );

    for (const element of pageRefs.current) if (element) observer.observe(element);

    return () => observer.disconnect();
  }, [onPageChange, pageCount, zoom]);

  return (
    <Document
      file={url}
      loading={<Loader />}
      onLoadSuccess={({ numPages }) => {
        setPageCount(numPages);
        onPageChange({ current: 1, total: numPages });
      }}
    >
      {Array.from({ length: pageCount }, (_, index) => (
        <div
          className="mb-6 w-fit bg-white last:mb-0"
          data-page={index + 1}
          key={index}
          ref={(element) => {
            pageRefs.current[index] = element;
          }}
        >
          <Page
            pageNumber={index + 1}
            renderAnnotationLayer={false}
            renderTextLayer={false}
            width={(PAGE_WIDTH_PX * zoom) / 100}
          />
        </div>
      ))}
    </Document>
  );
};
